import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { t, useLang } from "../lib/i18n";
import { Check, Loader2, Tag, BookOpen } from "lucide-react";

const MARQUEE_COVERS = [
  "https://covers.openlibrary.org/b/id/10523338-M.jpg",
  "https://covers.openlibrary.org/b/id/11481354-M.jpg",
  "https://covers.openlibrary.org/b/id/8231856-M.jpg",
  "https://covers.openlibrary.org/b/id/10520814-M.jpg",
  "https://covers.openlibrary.org/b/id/12646055-M.jpg",
  "https://covers.openlibrary.org/b/id/8228491-M.jpg",
  "https://covers.openlibrary.org/b/id/9259466-M.jpg",
  "https://covers.openlibrary.org/b/id/11153317-M.jpg",
  "https://covers.openlibrary.org/b/id/11502468-M.jpg",
  "https://covers.openlibrary.org/b/id/10110415-M.jpg",
  "https://covers.openlibrary.org/b/id/8775116-M.jpg",
  "https://covers.openlibrary.org/b/id/10316504-M.jpg",
  "https://covers.openlibrary.org/b/id/240727-M.jpg",
  "https://covers.openlibrary.org/b/id/8226191-M.jpg",
  "https://covers.openlibrary.org/b/id/12000084-M.jpg",
  "https://covers.openlibrary.org/b/id/8091016-M.jpg",
  "https://covers.openlibrary.org/b/id/10580367-M.jpg",
  "https://covers.openlibrary.org/b/id/12645114-M.jpg",
  "https://covers.openlibrary.org/b/id/10958396-M.jpg",
  "https://covers.openlibrary.org/b/id/14625765-M.jpg",
  "https://covers.openlibrary.org/b/id/12886731-M.jpg",
  "https://covers.openlibrary.org/b/id/14315535-M.jpg",
];

// Stagger covers per row so adjacent rows don't show the same titles.
function rotate(arr, n) { return arr.slice(n).concat(arr.slice(0, n)); }

export default function SubscribePage() {
  useLang();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  // If unchecked, we still take the payment but flip `plus_auto_renew=false`
  // immediately so nothing renews. When `plus_until` passes, the user
  // drops to free tier automatically.
  const [autoRenew, setAutoRenew] = useState(true);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data.plans || []));
    if (user && typeof user === "object") {
      api.get("/me/subscription").then(({ data }) => setSubscription(data));
    }
  }, [user]);

  // When the user switches plan, clear any applied promo (price changed)
  useEffect(() => {
    if (appliedPromo) {
      setAppliedPromo(null);
      setPromoInput("");
    }
  }, [selectedPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoError("");
    setValidatingPromo(true);
    try {
      const { data } = await api.post("/promo/validate", { code, plan: selectedPlan });
      setAppliedPromo({ code, ...data });
      toast.success("Promo applied");
    } catch (e) {
      setAppliedPromo(null);
      setPromoError(formatApiError(e));
    } finally {
      setValidatingPromo(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  const startCheckout = async () => {
    if (!user || typeof user !== "object") {
      navigate("/login", { state: { from: "/subscribe" } });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/create", {
        plan: selectedPlan,
        promo_code: appliedPromo?.code || "",
        origin_url: window.location.origin,
        auto_renew: autoRenew,
      });
      if (data.comped) {
        toast.success("Reads+ unlocked");
        await refresh();
        navigate("/subscribe/success?comped=1");
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  const isPlus = subscription?.is_plus;
  const currentPlan = plans.find((p) => p.id === selectedPlan);
  const displayAmount = appliedPromo ? appliedPromo.new_amount : currentPlan?.amount;

  return (
    <div className="relative" data-testid="subscribe-page">
      {/* Animated rows of real book covers — slow, full-width, atmospheric. */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0 flex flex-col justify-around gap-3 py-4 opacity-55">
          {[0, 1, 2, 3, 4, 5].map((row) => {
            const covers = rotate(MARQUEE_COVERS, row * 3);
            const dir = row % 2 === 0 ? "left" : "right";
            const duration = 110 + row * 14; // slow, varied per row
            return (
              <div
                key={row}
                className="flex gap-3 whitespace-nowrap"
                style={{ animation: `marquee-${dir} ${duration}s linear infinite` }}
              >
                {[...covers, ...covers, ...covers].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className="h-40 md:h-52 w-auto object-cover border border-[rgba(10,10,10,0.15)] shadow-md flex-shrink-0"
                    loading="eager"
                    decoding="async"
                    fetchpriority={i < 6 ? "high" : "low"}
                  />
                ))}
              </div>
            );
          })}
        </div>
        {/* Soft beige wash so the foreground text stays readable */}
        <div className="absolute inset-0 bg-[#F2EAD3]/35" />
        <style>{`
          @keyframes marquee-left  { from { transform: translateX(0); }     to { transform: translateX(-50%); } }
          @keyframes marquee-right { from { transform: translateX(-50%); }  to { transform: translateX(0); } }
        `}</style>
      </div>

      <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-16 fade-up bg-[#F2EAD3]/90 backdrop-blur-md rounded-sm shadow-[0_8px_60px_rgba(10,10,10,0.10)]">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#0A0A0A] border border-[#0A0A0A]/30 bg-[#E8DFC1] px-3 py-1.5 rounded-sm mb-5">
          <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("nav.plus")}
        </div>
        <h1 className="font-heading text-4xl md:text-6xl text-[#0A0A0A] tracking-tight leading-[1.02]">
          {t("sub.unlimited_books")}.<br/>{t("sub.unlimited_recs")}.
        </h1>
        <p className="text-[#525252] mt-5 max-w-xl mx-auto">
          {t("sub.subtitle")}
        </p>
        {isPlus && (
          <div className="mt-6 inline-flex items-center gap-2 font-mono uppercase tracking-widest text-xs text-[#0A0A0A]" data-testid="active-banner">
            <Check className="w-3.5 h-3.5" /> {t("sub.you_are_plus")} — expires {new Date(subscription.expires_at).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-8">
        {plans.map((p) => {
          const selected = selectedPlan === p.id;
          const showDiscount = selected && appliedPromo;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedPlan(p.id)}
              className={`text-left p-6 md:p-8 border rounded-sm transition-all ${selected ? "border-[#0A0A0A] bg-[#E8DFC1]/40 shadow-[0_0_0_3px_rgba(124,58,237,0.12)]" : "border-[rgba(0,0,0,0.12)] bg-[#FAF5E6] hover:border-[#0A0A0A]"}`}
              data-testid={`plan-${p.id}`}
            >
              <div className="flex items-baseline justify-between">
                <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{p.label}</div>
                {selected && <div className="w-4 h-4 rounded-full bg-[#0A0A0A] flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" /></div>}
              </div>
              <div className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3 tracking-tight">
                {showDiscount ? (
                  <>
                    <span className="text-[#A3A3A3] line-through text-2xl mr-2 align-middle">€{p.amount.toFixed(2)}</span>
                    €{appliedPromo.new_amount.toFixed(2)}
                  </>
                ) : (
                  <>€{p.amount.toFixed(2)}</>
                )}
                <span className="font-mono text-xs uppercase tracking-widest text-[#A3A3A3] ml-2">/ {p.id === "monthly" ? t("sub.monthly").toLowerCase() : t("sub.yearly").toLowerCase()}</span>
              </div>
              <div className="font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] mt-3">{p.tagline}</div>
            </button>
          );
        })}
      </div>

      {/* Promo code — text input, no hints */}
      <div className="max-w-md mx-auto mb-8" data-testid="promo-container">
        <label className="font-mono uppercase tracking-widest text-[10px] text-[#525252] block mb-2">Promo code</label>
        {appliedPromo ? (
          <div className="flex items-center justify-between gap-2 border border-[#0A0A0A]/40 bg-[#E8DFC1]/40 rounded-sm px-3 py-3" data-testid="promo-applied-box">
            <div className="flex items-center gap-2 min-w-0">
              <Tag className="w-3.5 h-3.5 text-[#0A0A0A] flex-shrink-0" strokeWidth={1.5} />
              <span className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A] truncate">{appliedPromo.code}</span>
              <span className="font-mono text-[11px] text-[#525252]">· {appliedPromo.discount_pct}% off applied</span>
            </div>
            <button onClick={removePromo} className="font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A]" data-testid="promo-remove">Remove</button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoInput}
                onChange={(e) => { setPromoInput(e.target.value); setPromoError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
                placeholder="Enter code"
                className="flex-1 bg-[#FAF5E6] border border-[rgba(0,0,0,0.12)] rounded-sm px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] font-mono uppercase tracking-wider"
                data-testid="promo-input"
              />
              <button
                onClick={applyPromo}
                disabled={validatingPromo || !promoInput.trim()}
                className="border border-[rgba(0,0,0,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-5 py-3 rounded-sm disabled:opacity-50 transition-colors"
                data-testid="promo-apply"
              >
                {validatingPromo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
              </button>
            </div>
            {promoError && (
              <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#B91C1C]" data-testid="promo-error">{promoError}</div>
            )}
          </>
        )}
      </div>

      {/* Auto-renew choice */}
      {!isPlus && (
        <div className="max-w-md mx-auto mb-6" data-testid="auto-renew-block">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#0A0A0A]"
              data-testid="auto-renew-toggle"
            />
            <span className="text-sm text-[#4A4030] leading-relaxed">
              <span className="font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A]">Auto-renew each period</span>
              <span className="block mt-1 text-[13px]">
                {autoRenew
                  ? "Reads+ renews automatically. Cancel any time from your Account page."
                  : "One-off pass. When this period ends, you'll drop back to the free tier — no surprises."}
              </span>
            </span>
          </label>
        </div>
      )}

      {/* CTA */}
      <div className="text-center">
        <button
          onClick={startCheckout}
          disabled={submitting || isPlus}
          className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white font-mono uppercase tracking-widest text-xs px-10 py-4 rounded-sm disabled:opacity-50 transition-colors"
          data-testid="checkout-button"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("sub.processing")}</>
            : isPlus ? t("sub.you_are_plus")
            : displayAmount === 0 ? "Activate Reads+ (free)"
            : `${t("sub.subscribe")} — €${displayAmount?.toFixed(2)}`}
        </button>
        <div className="mt-4 font-mono uppercase tracking-widest text-[10px] text-[#A3A3A3]">
          Cancel anytime · Secure payment by Stripe
        </div>
      </div>

      {/* Comparison */}
      <div className="grid md:grid-cols-2 gap-px bg-[rgba(0,0,0,0.08)] border border-[rgba(0,0,0,0.08)] mt-20">
        <div className="bg-[#FAF5E6] p-8 md:p-10">
          <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] mb-2">Free</div>
          <div className="font-heading text-2xl text-[#0A0A0A] mb-6">Get started</div>
          <ul className="space-y-3 text-sm text-[#0A0A0A]">
            <FeatureItem>Add up to 10 books</FeatureItem>
            <FeatureItem>50 recommendations</FeatureItem>
            <FeatureItem>Unlimited community reviews</FeatureItem>
            <FeatureItem>Author profiles</FeatureItem>
          </ul>
        </div>
        <div className="bg-[#FAF5E6] p-8 md:p-10 relative">
          <div className="absolute top-6 right-6 font-mono uppercase tracking-widest text-[10px] text-[#0A0A0A] bg-[#E8DFC1] px-2 py-1 rounded-sm">Reads+</div>
          <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#0A0A0A] mb-2">Plus</div>
          <div className="font-heading text-2xl text-[#0A0A0A] mb-6">Unlock everything</div>
          <ul className="space-y-3 text-sm text-[#0A0A0A]">
            <FeatureItem brand>Unlimited book additions</FeatureItem>
            <FeatureItem brand>Unlimited recommendations</FeatureItem>
            <FeatureItem brand>Priority recommendation refresh</FeatureItem>
            <FeatureItem brand>Support the project</FeatureItem>
          </ul>
        </div>
      </div>
      </div>
    </div>
  );
}

function FeatureItem({ children, brand }) {
  return (
    <li className="flex items-start gap-2.5">
      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${brand ? "text-[#0A0A0A]" : "text-[#0A0A0A]"}`} strokeWidth={2} />
      <span>{children}</span>
    </li>
  );
}
